import React, { useState } from 'react'
import NonModalWindow from '../NonModalWindow'
import { useForm, useWatch } from 'react-hook-form'
import CloseConfirmationModal from '../CloseConfirmationModal'
import { useLocation, useSearchParams } from 'react-router-dom'
import useCloseToOrigin from '@/hooks/useCloseToOrigin'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { XMarkIcon } from '@heroicons/react/16/solid'
import { Button } from '../ui/button'
import Field from '../ui/Field'
import { Input } from '../ui/Input'
import { Label } from '../ui/Label'
import FieldStatusMessage from '../ui/FieldStatusMessage'
import useCreateVaultGroup from '@/services/api/vault/useCreateVaultGroup'
import useCreateCategoryGroup from '@/services/api/transaction-category/useCreateCategoryGroup'

const schema = yup.object().shape({
  name: yup.string().required(),
  description: yup.string().optional(),
})

export default function Creategroup({
  groupType,
}: {
  groupType: 'vault' | 'transactionCategory'
}) {
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)
  const closeToOrigin = useCloseToOrigin()
  const location = useLocation()

  const [searchParams] = useSearchParams()
  const type = searchParams.get('type') //in|out

  const { action: createVault, status: createVaultStatus } =
    useCreateVaultGroup()

  const { action: createCategoryGroup, status: createCategoryGroupStatus } =
    useCreateCategoryGroup()

  const {
    control,
    register,
    handleSubmit,
    formState: { isDirty, errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      description: '',
    },
  })

  const groupName = useWatch({
    control,
    name: 'name',
  })

  const description = useWatch({
    control,
    name: 'description',
  })

  async function onSubmit(vaules) {
    if (groupType === 'vault') {
      await createVault({ name: vaules.name, description: vaules.name })
    } else {
      await createCategoryGroup({
        name: vaules.name,
        description: vaules.name,
        type:
          type === 'in' ? 'income' : type === 'out' ? 'expenses' : undefined,
      })
    }
    closeToOrigin()
  }

  const [name, setName] = useState('')

  return (
    <>
      <CloseConfirmationModal
        open={showConfirmationModal}
        setOpen={setShowConfirmationModal}
        onConfirm={() => {
          setShowConfirmationModal(false)
          closeToOrigin()
        }}
      />

      <NonModalWindow
        isDirty={isDirty}
        isLoading={false}
        as="form"
        onSubmit={handleSubmit(onSubmit)}
        title={`Add group for ${groupType === 'vault' ? 'vault' : `${type === 'in' ? 'Money In' : 'Money Out'} category`}`}
        onClose={() => {
          setShowConfirmationModal(isDirty)
          // setNonModalWindowState('closed')
        }}
      >
        <NonModalWindow.Body className="pt-2">
          <div className="gap-4- flex flex-col gap-field-gap">
            <Field
              status={errors.name?.message ? 'error' : undefined}
              statusText={errors.name?.message}
              className="group"
            >
              <Label className="flex items-center justify-between">
                <span>Group Name</span>
                <span className="hidden text-txt-secondary group-focus-within:inline">
                  {groupName.length}/30
                </span>
              </Label>
              <Input placeholder="Name" maxLength={30} {...register('name')} />
              <FieldStatusMessage />
            </Field>

            <Field
              status={errors.description?.message ? 'error' : undefined}
              statusText={errors.description?.message}
              className="group"
            >
              <Label className="flex items-center justify-between">
                <span>Group Description</span>
                <span className="hidden text-txt-secondary group-focus-within:inline">
                  {description.length}/30
                </span>
              </Label>
              <Input
                placeholder="Description"
                maxLength={30}
                {...register('description')}
              />
              <FieldStatusMessage />
            </Field>
          </div>
        </NonModalWindow.Body>
        <NonModalWindow.Footer>
          <Button
            type="button"
            variant="secondary"
            onClick={() => {
              setShowConfirmationModal(isDirty)
              if (!isDirty) {
                closeToOrigin()
              }
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            loading={createVaultStatus.status === 'pending'}
          >
            Save
          </Button>
        </NonModalWindow.Footer>
      </NonModalWindow>
    </>
  )
}
